import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ShieldOff } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { EmptyState } from '@/components/data/EmptyState'
import { AlertsFilters } from '@/components/mlro/AlertsFilters'
import { MlroAlertsQueue } from '@/components/mlro/MlroAlertsQueue'
import { useRoles } from '@/hooks/useRoles'
import { MLRO_ALERT_SEVERITIES, type MlroAlertListFilters } from '@/types/mlro'
import { searchEnum, searchString } from '@/lib/search-params'

export const Route = createFileRoute('/mlro/alerts')({
    component: MlroAlertsPage,
    validateSearch: (search): MlroAlertListFilters => ({
        category: searchString(search.category),
        severity: searchEnum(search.severity, MLRO_ALERT_SEVERITIES),
        acknowledged: searchEnum(search.acknowledged, ['true', 'false'] as const),
    }),
})

function MlroAlertsPage() {
    const filters = Route.useSearch()
    const navigate = useNavigate({ from: Route.fullPath })
    const { hasAnyRole } = useRoles()

    if (!hasAnyRole('mlro')) {
        return (
            <EmptyState
                icon={ShieldOff}
                title="No access"
                description="The MLRO surfaces are restricted to operators with the MLRO role."
            />
        )
    }

    const setFilters = (next: MlroAlertListFilters) => {
        navigate({
            search: () => prune(next),
            replace: true,
        })
    }

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold">Alerts</h1>
                <p className="text-muted-foreground text-sm">
                    Compliance alerts emitted by the gaming service. Acknowledge once triaged, or
                    open a case for follow-up.
                </p>
            </div>

            <AlertsFilters value={filters} onChange={setFilters} />

            <Card>
                <CardHeader>
                    <CardTitle>Recent alerts</CardTitle>
                </CardHeader>
                <CardContent>
                    <MlroAlertsQueue filters={filters} />
                </CardContent>
            </Card>
        </div>
    )
}

function prune(filters: MlroAlertListFilters): MlroAlertListFilters {
    const out: MlroAlertListFilters = {}
    if (filters.category) out.category = filters.category
    if (filters.severity) out.severity = filters.severity
    if (filters.acknowledged) out.acknowledged = filters.acknowledged
    return out
}
